import { Fragment, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Flex,
  Form,
  OptionCard,
  OptionCardDescription,
  OptionCardGroup,
  OptionCardTitle,
  PanelHeader,
  PanelSection,
  PanelTitle,
  Paragraph,
  Strong,
} from '@customerio/pluma-components/react';
import { useSignupFlow } from '../signup-flow';
import { onboardingUsageSubmittedProps, SignupAnalyticsEvent, signupTypeProps } from '../utils/analytics';
import { DATA_CENTERS, MESSAGING_USE_CASES } from './onboarding-usage-options';
import * as styles from './onboarding-usage-step.css';

const HEADING = 'How are you planning on using Customer.io?';
const DATA_CENTER_HEADING = 'Choose a data center';

export function OnboardingUsageStep() {
  const {
    formValues,
    patchFormValues,
    messagingUseCases,
    setMessagingUseCases,
    effectiveSignupType,
    resolvedSignupType,
    goToNextStep,
    trackSignupEvent,
  } = useSignupFlow();

  // Fire once per step entry — the analog of Ember's fire-on-route-transition
  // analytics; the ref keeps StrictMode's double-invoked effects from
  // double-counting the funnel.
  const hasTrackedView = useRef(false);
  useEffect(() => {
    if (hasTrackedView.current) {
      return;
    }
    hasTrackedView.current = true;
    trackSignupEvent(SignupAnalyticsEvent.OnboardingUsageViewed, signupTypeProps({ signupType: effectiveSignupType }));
  }, [trackSignupEvent, effectiveSignupType]);

  // "Unset" for `dataCenter` is the empty string, never null — see the note on
  // `SignupFormValues`.
  const isSubmitDisabled = formValues.dataCenter === '' || messagingUseCases.length === 0;

  const onSubmit = async () => {
    trackSignupEvent(
      SignupAnalyticsEvent.OnboardingUsageSubmitted,
      onboardingUsageSubmittedProps({
        signupType: effectiveSignupType,
        usage: messagingUseCases,
        dataCenter: formValues.dataCenter,
        resolvedSignupType,
      }),
    );
    await goToNextStep();
  };

  return (
    <Box data-testid="signup-step-onboarding-usage">
      <PanelHeader>
        <PanelTitle level="1">{HEADING}</PanelTitle>
      </PanelHeader>
      <PanelSection>
        <Form onSubmit={onSubmit}>
          <Paragraph variant="secondary" sizeVariant="xs" mb="200">
            Select all that apply.
          </Paragraph>

          <OptionCardGroup
            ariaLabel={HEADING}
            inputType="checkbox"
            cardsLayout="grid"
            cardsColumns={2}
            cardsGap="100"
            value={messagingUseCases}
            onChange={(values: string[]) => setMessagingUseCases(values)}
          >
            {MESSAGING_USE_CASES.map((option) => (
              <OptionCard
                key={option.value}
                className={styles.useCaseCard}
                value={option.value}
                ariaLabel={option.label}
                icon={option.icon}
                w="full"
              >
                <OptionCardTitle>{option.label}</OptionCardTitle>
                <OptionCardDescription>
                  {option.descriptionLines.map((line, index) => (
                    <Fragment key={line === '' ? `blank-${index}` : line}>
                      {index > 0 ? <br /> : null}
                      {line === '' ? <span aria-hidden="true">&nbsp;</span> : line}
                    </Fragment>
                  ))}
                </OptionCardDescription>
              </OptionCard>
            ))}
          </OptionCardGroup>

          <Box className={styles.dataCenterSection}>
            <Paragraph variant="active" color="base" mb="050">
              {DATA_CENTER_HEADING}
            </Paragraph>
            <Paragraph variant="secondary" color="base" mb="200">
              Consider where your business &amp; customers are located, plus any compliance regulations (GDPR, etc).
              <Strong> You won&apos;t be able to change this later.</Strong>
            </Paragraph>

            <OptionCardGroup
              ariaLabel={DATA_CENTER_HEADING}
              inputType="radio"
              cardsGap="150"
              value={formValues.dataCenter}
              onChange={(value: string) => patchFormValues({ dataCenter: value })}
            >
              {DATA_CENTERS.map((option) => (
                <OptionCard
                  key={option.value}
                  value={option.value}
                  title={`${option.flag} ${option.name}`}
                  // The flag is decoration; the region name alone is the label.
                  ariaLabel={option.name}
                  w="full"
                />
              ))}
            </OptionCardGroup>
          </Box>

          <Flex justifyContent="flex-end" mt="300">
            <Button
              type="submit"
              variant="primary"
              icon="chevron-right"
              iconPosition="trailing"
              isDisabled={isSubmitDisabled}
            >
              Continue
            </Button>
          </Flex>
        </Form>
      </PanelSection>
    </Box>
  );
}
